/**
 * License search page with filters and results table
 */
import { useState, FormEvent, useEffect, useCallback, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { SortableHeader } from '../components/SortableHeader'
import {
  searchLicenses,
  exportLicensesToCSV,
  listSavedSearches,
  createSavedSearch,
  deleteSavedSearch,
  toggleSavedSearchWatch,
  toggleSavedSearchFeedsLeads,
  getLicenseFacets,
} from '../services/licenses'
import { createTrackedLead, convertLead, bulkCreateTrackedLeads, matchClients, linkLicense } from '../services/crm'
import { getLicenseAnnotations } from '../services/licenses'
import type { LicenseAnnotations } from '../services/licenses'
import { geocodeQuery } from '../services/geocode'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import type { ClientMatchResult, MatchedSite } from '../types'
import type {
  FCCLicense, LicenseSearchParams, SavedSearch,
  LicenseFacets, SearchMode,
} from '../types'
import { LICENSE_STATUS, RADIO_SERVICES } from '../types'
import { format } from 'date-fns'
import Modal, { ModalFooter } from '../components/Modal'
import PageHeading from '../components/PageHeading'
import { SEARCH_HELP } from '../content/pageHelp'
import { XIcon, BriefcaseIcon } from '../components/Icons'
import { Pagination } from '../components/Pagination'
import DncToggle, { DncBadge } from '../components/DncToggle'
import {
  DncField, FrequencyField,
  PresetDateRange, PrimaryFilters, TextFilterField, type ExpiryPreset,
} from '../components/search/SearchFilters'
import { DEFAULT_FREQUENCY_TOLERANCE_KHZ, parseFrequency } from '../utils/frequency'

// ---------------------------------------------------------------------------
// Icon components unique to this page
// ---------------------------------------------------------------------------
function UserPlusIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
    </svg>
  )
}
function BellIcon({ active }: { active: boolean }) {
  return active ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.450l1.514 1.514a4 4 0 00-5.478-5.478zM7.584 9H4a1 1 0 00-.707 1.707L4 11.414V14a1 1 0 01-1 1H2a1 1 0 000 2h16a1 1 0 000-2h-1a1 1 0 01-1-1v-2.586l.707-.707A1 1 0 0016 9h-3.584A4.015 4.015 0 0010 8a4.015 4.015 0 00-2.416 1z" clipRule="evenodd" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Session storage — preserve search state across navigation
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------

/**
 * One frequency, the way a radio person reads it.
 *
 * FCC stores `NUMERIC(12,5)`, but a channel is conventionally written to four
 * decimals — 152.4800, 462.5625 — and a bare `152.48` looks like a different,
 * vaguer number to somebody used to reading channel lists. So: four decimals
 * normally, five only when the fifth carries information (152.40550 exists in the
 * real data and rounding it away would misreport what the license is on).
 */
function formatMatchedFrequency(value: number): string {
  const fifthDecimalIsSignificant = Math.round(value * 100000) % 10 !== 0
  return value.toFixed(fifthDecimalIsSignificant ? 5 : 4)
}

/**
 * The "Match" cell: what put this row in these results.
 *
 * Deliberately not a static Frequency column. A license in a frequency search
 * holds 4 assignments on average and up to 49, of which up to 12 may match, so a
 * column showing "the" frequency would show one and hide the rest without saying
 * so. The first match is shown, the count of the others beside it, and all of
 * them in the tooltip.
 *
 * A band grant, when the search asked for them, is shown as a RANGE and greyed:
 * "authorised anywhere in 150.0000–174.0000" is a weaker claim than "assigned
 * 152.4800", and a table that renders them identically misreports the first as
 * the second.
 *
 * When both filters are in play both values appear. That is also the cheapest way
 * to see a known limitation: the two filters are independent `EXISTS`, so they
 * are ANDed at the LICENSE level rather than the site level — a license can
 * satisfy "on 154.57" from one transmitter and "within 10 miles" from another.
 * Showing both values is what makes that visible instead of merely documented.
 */
/** Miles, with a thousands separator — a cross-site match can be four digits. */
function formatMiles(miles: number | null): string {
  if (miles == null) return ''
  return `${miles.toLocaleString(undefined, { maximumFractionDigits: 1 })} mi`
}

function MatchCell({ frequencies, bands, miles, sites }: {
  frequencies?: number[]
  bands?: number[][]
  /** Distance to the license's NEAREST site. Used only when there is no frequency
      search, where "which site" has no meaning. */
  miles?: number
  /** Where the matched frequencies actually are. Preferred over `miles` whenever
      present, because it answers the question the row is actually making. */
  sites?: MatchedSite[]
}) {
  const parts: ReactNode[] = []
  if (frequencies && frequencies.length > 0) {
    const all = frequencies.map(formatMatchedFrequency)
    parts.push(
      <span key="freq" title={all.length > 1 ? all.join(', ') : undefined}>
        <span className="font-mono">{all[0]}</span>
        {all.length > 1 && (
          <span className="ml-1 text-xs text-gray-500">+{all.length - 1}</span>
        )}
      </span>,
    )
  }
  if (bands && bands.length > 0) {
    // Rendered as a RANGE, and greyed, because it is a weaker claim: this
    // licensee is authorised across a band that happens to cover the frequency,
    // not assigned to it. Showing "150.0000" here as though it were a channel is
    // what made a search for 152.480 read as 284 users when 90 was the answer.
    const all = bands.map(
      ([low, high]) => `${formatMatchedFrequency(low)}–${formatMatchedFrequency(high)}`,
    )
    parts.push(
      <span
        key="band"
        className="text-gray-500"
        title={`Authorized across a band covering this frequency${
          all.length > 1 ? `: ${all.join(', ')}` : ''
        }`}
      >
        <span className="font-mono text-xs">{all[0]}</span>
        {all.length > 1 && <span className="ml-1 text-xs">+{all.length - 1}</span>}
        <span className="ml-1 text-xs italic">band</span>
      </span>,
    )
  }
  if (sites && sites.length > 0) {
    // The distance to the site holding the MATCHED frequency, not to the license's
    // nearest site. Those are different numbers, and showing the second as though it
    // were the first is how a search for 452.4875 near Denver reported the National
    // Football League as "1.6 mi" when that assignment is in Honolulu.
    const best = sites[0]
    const extra = sites.length > 1 ? ` (+${sites.length - 1} more)` : ''
    if (best.reason === 'elsewhere') {
      parts.push(
        <span
          key="site"
          className="text-amber-700"
          title={`This license has a site inside the radius, but the matched frequency is at a different site${best.where ? `: ${best.where}` : ''}${extra}`}
        >
          {formatMiles(best.miles)}
          {best.where && <span className="ml-1 text-xs">({best.where})</span>}
        </span>,
      )
    } else if (best.miles != null) {
      parts.push(
        <span key="site" title={`Distance to the site holding this frequency${extra}`}>
          {formatMiles(best.miles)}
        </span>,
      )
    } else {
      // Two different reasons we cannot place it, kept apart on purpose:
      // site_missing is our own import dropping the location row, and should
      // shrink when that is fixed; site_unplaceable is an area or mobile
      // authorisation, which genuinely is not at a point and never will be.
      const label = best.reason === 'site_missing'
        ? 'site not in our data'
        : 'site not located'
      parts.push(
        <span
          key="site"
          className="text-gray-500 text-xs italic"
          title={
            best.reason === 'site_missing'
              ? 'The FCC record names a transmitter site we do not hold, so this match cannot be placed on a map. It is shown rather than hidden, because dropping it would remove most of the 800/900 MHz band from geographic search.'
              : 'A mobile or area-of-operation authorisation. It has no single coordinate, so no distance can be given.'
          }
        >
          {label}
        </span>,
      )
    }
  } else if (miles != null) {
    parts.push(<span key="dist">{miles.toFixed(1)} mi</span>)
  }
  if (parts.length === 0) return <span className="text-gray-400">—</span>
  return (
    <span className="whitespace-nowrap">
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-1.5 text-gray-300">·</span>}
          {part}
        </span>
      ))}
    </span>
  )
}

/**
 * Build rowActionStatus and rowActionIds from server-side CRM annotations.
 *
 * Typed to the annotation fields alone, not to the full search response, so the
 * batch refresh (`getLicenseAnnotations`) feeds the identical reducer. Two readers
 * of the same facts is how a badge comes to mean one thing after a search and
 * another after a Back.
 */
function buildCrmRowStatus(response: LicenseAnnotations): {
  status: Record<number, string>
  ids: Record<number, number>
} {
  const status: Record<number, string> = {}
  const ids: Record<number, number> = {}
  // Prefer the id-map (richer) over bare USI lists
  for (const [usiStr, id] of Object.entries(response.lead_usi_to_id ?? {})) {
    const usi = Number(usiStr)
    status[usi] = 'lead'
    ids[usi] = id
  }
  for (const usi of response.lead_usis ?? []) {
    if (!status[usi]) status[usi] = 'lead'
  }
  for (const [usiStr, id] of Object.entries(response.client_usi_to_id ?? {})) {
    const usi = Number(usiStr)
    status[usi] = 'client'
    ids[usi] = id
  }
  for (const usi of response.client_usis ?? []) {
    if (!status[usi]) status[usi] = 'client'
  }
  return { status, ids }
}

const SEARCH_CACHE_KEY = 'fcc_search_cache'

/** How long typing has to pause before a search runs. The value every list page uses. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * The shortest term worth searching.
 *
 * Three, matching `party_filter.FTS_MIN_NAME_LENGTH` on the server — below it, full
 * text matches nothing useful and a two-letter prefix matches a sixth of the table.
 * Two-letter state codes are the deliberate exception and are handled server-side, so
 * "CO" reaches the box's routing rather than being blocked here.
 */
const MIN_SEARCH_LENGTH = 3

/**
 * The expiration window the form starts on, and returns to when cleared.
 *
 * A renewal desk opening this page is nearly always asking "what is coming up?", so
 * an empty form is a wasted first screen. Four months is the window this shop works
 * to. Keep in step with `date_presets.DEFAULT_EXPIRATION_PRESET` on the server.
 *
 * It is stored as the *preset key*, never as the two dates it resolves to, so a tab
 * left open overnight still means "the next 120 days" in the morning — the same
 * reason saved searches store the key.
 */
const DEFAULT_EXPIRY_PRESET = 'next_120d'

/** The starting search: today's default window, nothing else. */
function initialSearchParams(limit = 25): LicenseSearchParams {
  const range = EXPIRY_RANGE_PRESETS.find((p) => p.value === DEFAULT_EXPIRY_PRESET)?.range() ?? {}
  return {
    page: 1,
    limit,
    expiration_preset: DEFAULT_EXPIRY_PRESET,
    expiration_start: range.start,
    expiration_end: range.end,
  }
}


interface SearchCache {
  searchParams: LicenseSearchParams
  /** The box's contents and which kind of match produced these results. */
  searchTerm?: string
  searchMode?: SearchMode
  datePreset: string
  grantPreset: string
  geoQuery: string
  geoResolved: string
  geoLat: string
  geoLon: string
  geoRadius: number
  advancedOpen: boolean
  results: FCCLicense[]
  totalResults: number
  totalPages: number
  /** The match annotations that go with `results`. Cached alongside them because
   *  a restored page that lost its Match column would silently drop the one thing
   *  explaining why those rows are there. */
  matchedFrequencies?: Record<number, number[]>
  matchedBands?: Record<number, number[][]>
  matchedDistances?: Record<number, number>
  matchedSites?: Record<number, MatchedSite[]>
}

// --- Expiration date range ----------------------------------------------------
//
// This used to be two independent selects, "Expiring After" and "Expiring Before",
// whose presets were framed differently: After offered offsets in both directions
// ("90 days ago", "Today", "30 days from now") while Before offered only forward
// windows ("Next 30 days", "This month"). People read them as one range, so
// picking "After: 30 days from now" with "Before: Next 30 days" — which both land
// on the same day — silently returned nothing, and the only way to find overdue
// licenses was through a control labelled "After".
//
// Now: one preset list where every entry sets BOTH ends and reads as a complete
// phrase, feeding two date inputs that stay visible so a preset is never a black
// box.
//
// The dates a preset produces are shown but **disabled**, with an "Edit dates" link
// that switches to "Custom range…" and unlocks them. That is deliberate: a preset is
// an intent ("the next 90 days"), and the key — not the dates — is what gets saved
// and what the server re-resolves on every run. Leaving the inputs live made the
// resolved dates look like the thing being stored, which is precisely the confusion
// behind the frozen-saved-search bug. Visible keeps the preset honest; disabled keeps
// it from reading as editable state. See app/services/date_presets.py.
//
// Backend mapping: expiration_preset wins if set, otherwise expiration_start →
// expired_date >= start and expiration_end → <= end.

const CUSTOM_RANGE = 'custom'

/**
 * Format a Date as YYYY-MM-DD in the *local* timezone.
 *
 * The old code used `toISOString().slice(0, 10)`, which converts to UTC first —
 * so in Colorado, any preset evaluated after ~6pm local resolved to the following
 * day and "Today" meant tomorrow.
 */
function toDateInput(d: Date): string {
  const month = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

function localToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

/** Today shifted by `n` days (negative = past), as YYYY-MM-DD. */
function daysFromToday(n: number): string {
  const d = localToday()
  d.setDate(d.getDate() + n)
  return toDateInput(d)
}

/** First day of the month `offset` months from now (0 = this month). */
function startOfMonth(offset: number): string {
  const d = localToday()
  return toDateInput(new Date(d.getFullYear(), d.getMonth() + offset, 1))
}

/** Last day of the month `offset` months from now (day 0 of the next month). */
function endOfMonth(offset: number): string {
  const d = localToday()
  return toDateInput(new Date(d.getFullYear(), d.getMonth() + offset + 1, 0))
}

// `ExpiryPreset` and the component that renders a range now live in
// components/search/SearchFilters.tsx, alongside the fields it arranges. These two
// arrays stay HERE on purpose: backend/app/tests/test_date_presets.py reads this file
// to assert the dropdown's keys match the Python resolver's, and a filter key that
// exists on only one side is invisible until it misbehaves.
const EXPIRY_RANGE_PRESETS: ExpiryPreset[] = [
  { value: '', label: 'Any expiration date', range: () => ({}) },
  { value: 'next_14d', label: 'Expiring in the next 14 days',
    range: () => ({ start: daysFromToday(0), end: daysFromToday(14) }) },
  { value: 'next_30d', label: 'Expiring in the next 30 days',
    range: () => ({ start: daysFromToday(0), end: daysFromToday(30) }) },
  { value: 'next_60d', label: 'Expiring in the next 60 days',
    range: () => ({ start: daysFromToday(0), end: daysFromToday(60) }) },
  { value: 'next_90d', label: 'Expiring in the next 90 days',
    range: () => ({ start: daysFromToday(0), end: daysFromToday(90) }) },
  { value: 'next_120d', label: 'Expiring in the next 120 days',
    range: () => ({ start: daysFromToday(0), end: daysFromToday(120) }) },
  { value: 'this_month', label: 'Expiring this month',
    range: () => ({ start: daysFromToday(0), end: endOfMonth(0) }) },
  { value: 'next_month', label: 'Expiring next month',
    range: () => ({ start: startOfMonth(1), end: endOfMonth(1) }) },
  { value: 'this_year', label: 'Expiring this year',
    range: () => ({ start: daysFromToday(0), end: `${localToday().getFullYear()}-12-31` }) },
  { value: 'around_90d', label: 'Expired or expiring within 90 days',
    range: () => ({ start: daysFromToday(-90), end: daysFromToday(90) }) },
  { value: 'expired_90d', label: 'Already expired (past 90 days)',
    range: () => ({ start: daysFromToday(-90), end: daysFromToday(0) }) },
  { value: 'expired_any', label: 'Already expired (any date)',
    range: () => ({ end: daysFromToday(0) }) },
  { value: CUSTOM_RANGE, label: 'Custom range…', range: () => ({}) },
]

/**
 * Grant-date presets. A grant is always in the past, so every window looks backwards
 * — the mirror image of the expiry list, which mostly looks forwards.
 *
 * Keys must match GRANT_PRESETS in backend/app/services/date_presets.py;
 * test_date_presets.py fails the build if the two lists drift apart.
 */
const GRANT_RANGE_PRESETS: ExpiryPreset[] = [
  { value: '', label: 'Any grant date', range: () => ({}) },
  { value: 'granted_last_30d', label: 'Granted in the last 30 days',
    range: () => ({ start: daysFromToday(-30), end: daysFromToday(0) }) },
  { value: 'granted_last_90d', label: 'Granted in the last 90 days',
    range: () => ({ start: daysFromToday(-90), end: daysFromToday(0) }) },
  { value: 'granted_last_180d', label: 'Granted in the last 6 months',
    range: () => ({ start: daysFromToday(-180), end: daysFromToday(0) }) },
  { value: 'granted_last_365d', label: 'Granted in the last year',
    range: () => ({ start: daysFromToday(-365), end: daysFromToday(0) }) },
  { value: 'granted_this_year', label: 'Granted this year',
    range: () => ({ start: `${localToday().getFullYear()}-01-01`, end: daysFromToday(0) }) },
  { value: 'granted_last_year', label: 'Granted last year',
    range: () => ({
      start: `${localToday().getFullYear() - 1}-01-01`,
      end: `${localToday().getFullYear() - 1}-12-31`,
    }) },
  { value: CUSTOM_RANGE, label: 'Custom range…', range: () => ({}) },
]

/**
 * Which preset, if any, produces exactly this pair of dates *today*?
 *
 * Only a guess, and only a fallback. It is right for dates that arrive without a
 * stored key — a restored session, a link from another page, a saved search from
 * before presets were persisted — and it is wrong the moment those dates are a day
 * old, which is exactly how a saved "next 90 days" used to reappear as "Custom
 * range…". Prefer the stored `*_preset` key whenever there is one.
 */
function derivePreset(presets: ExpiryPreset[], start?: string, end?: string): string {
  if (!start && !end) return ''
  for (const preset of presets) {
    if (preset.value === '' || preset.value === CUSTOM_RANGE) continue
    const { start: pStart, end: pEnd } = preset.range()
    if ((pStart ?? '') === (start ?? '') && (pEnd ?? '') === (end ?? '')) return preset.value
  }
  return CUSTOM_RANGE
}

/**
 * The preset to show for a date pair: the stored key if the params carry one,
 * otherwise a guess from the dates themselves.
 */
function presetFor(
  presets: ExpiryPreset[], stored?: string, start?: string, end?: string,
): string {
  if (stored && presets.some((p) => p.value === stored)) return stored
  return derivePreset(presets, start, end)
}

/**
 * Adopt a guessed preset into the params, so a range that *displays* as relative is
 * also *stored* as relative.
 *
 * Dates can arrive already resolved from callers that mean a relative range but have
 * no way to say so — the dashboard's "Expiring in 90 days" cards navigate here with
 * `expiration_start: today, expiration_end: today+90` (`DashboardPage.tsx`). Without
 * this the dropdown would read "Expiring in the next 90 days" while Save This Search
 * stored two fixed dates: the original bug, re-entering through a side door.
 *
 * Only applied to freshly-arrived params, and only when the dates match a preset
 * exactly today — which is the same rule the dropdown uses to pick its own label, so
 * what is stored always matches what is shown.
 */
function adoptDerivedPresets(params: LicenseSearchParams): LicenseSearchParams {
  const next = { ...params }
  const expiry = presetFor(
    EXPIRY_RANGE_PRESETS, next.expiration_preset, next.expiration_start, next.expiration_end,
  )
  const grant = presetFor(
    GRANT_RANGE_PRESETS, next.grant_date_preset, next.grant_date_start, next.grant_date_end,
  )
  next.expiration_preset = isRelativePreset(expiry) ? expiry : undefined
  next.grant_date_preset = isRelativePreset(grant) ? grant : undefined
  return next
}

/** A named preset is active (so its dates are derived, not typed). */
function isRelativePreset(value: string): boolean {
  return value !== '' && value !== CUSTOM_RANGE
}

/**
 * Recompute the dates for any relative preset the params carry, as of today.
 *
 * Params can arrive stale — a saved search from last month, a session cache from
 * yesterday, a link from another page. The server re-resolves the preset when it
 * runs the query regardless; this keeps what the form *shows* in step with what it
 * will actually search. Dates without a preset are left exactly as they are.
 */
function resolvePresetDates(params: LicenseSearchParams): LicenseSearchParams {
  const next = { ...params }
  const expiry = EXPIRY_RANGE_PRESETS.find(
    (p) => p.value === next.expiration_preset && isRelativePreset(p.value),
  )
  if (expiry) {
    const { start, end } = expiry.range()
    next.expiration_start = start
    next.expiration_end = end
  }
  const grant = GRANT_RANGE_PRESETS.find(
    (p) => p.value === next.grant_date_preset && isRelativePreset(p.value),
  )
  if (grant) {
    const { start, end } = grant.range()
    next.grant_date_start = start
    next.grant_date_end = end
  }
  return next
}

export default function SearchPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useState<LicenseSearchParams>(
    () => initialSearchParams(),
  )
  /** Update one or more filters, leaving the rest of the search alone. */
  const patchParams = useCallback((patch: Partial<LicenseSearchParams>) => {
    setSearchParams((prev) => ({ ...prev, ...patch }))
  }, [])
  // Which named range is selected. '' = any date, CUSTOM_RANGE = the From/To dates
  // are literal. Mirrored into searchParams.expiration_preset / grant_date_preset,
  // which is what gets sent, saved, and re-resolved server-side on every run.
  /**
   * The one search box.
   *
   * Held outside `searchParams` because it drives its own request: the raw value keeps
   * the input responsive, and the debounced one is what actually gets searched.
   */
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS)

  /**
   * Which kind of match produced what is on screen.
   *
   * Typing always returns to `starts`; the Search button sets `anywhere` and it holds
   * until the term changes. Paging and sorting carry it, so page 2 of a thorough
   * search is still a thorough search.
   */
  const [searchMode, setSearchMode] = useState<SearchMode>('starts')

  const [datePreset, setDatePreset] = useState(DEFAULT_EXPIRY_PRESET)
  const [grantPreset, setGrantPreset] = useState('')
  const [results, setResults] = useState<FCCLicense[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  /**
   * Why each row matched: the frequencies that satisfied a frequency search, and
   * the distance to the nearest site that satisfied a radius search.
   *
   * Both are keyed by USI and both are display-only — the server computes them
   * after selecting the page, so nothing here can affect which licenses came back.
   * They exist because neither answer is a property of the license: a license in
   * these results holds 4 frequency assignments on average and up to 49, of which
   * several may match, and it has as many transmitter sites. "The frequency" and
   * "the distance" are properties of the match.
   */
  const [matchedFrequencies, setMatchedFrequencies] =
    useState<Record<number, number[]>>({})
  const [matchedBands, setMatchedBands] =
    useState<Record<number, number[][]>>({})
  const [matchedDistances, setMatchedDistances] =
    useState<Record<number, number>>({})
  /** Cached with the other match annotations, not re-fetched like the CRM badges:
      this depends only on the search parameters and FCC data, so it cannot go stale
      the way a Do Not Contact flag can. */
  const [matchedSites, setMatchedSites] =
    useState<Record<number, MatchedSite[]>>({})
  const [doNotRenewUsis, setDoNotRenewUsis] = useState<Set<number>>(new Set())
  const [dncCallSigns, setDncCallSigns] = useState<Set<string>>(new Set())
  const [pendingRenewalCallSigns, setPendingRenewalCallSigns] = useState<Set<string>>(new Set())
  const [dncToastMsg, setDncToastMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Geographic radius search state
  const [geoQuery, setGeoQuery] = useState('')       // text the user typed
  const [geoResolved, setGeoResolved] = useState('') // display name after geocoding
  const [geoLat, setGeoLat] = useState('')
  const [geoLon, setGeoLon] = useState('')
  const [geoRadius, setGeoRadius] = useState(25)
  const [geoLoading, setGeoLoading] = useState(false)
  const [geoError, setGeoError] = useState('')

  /**
   * The frequency box, and how wide a window counts as a match.
   *
   * The text is held separately because a half-typed "154." is not a number yet, but
   * unlike the radius above, the parsed value is written straight into `searchParams`
   * on every keystroke rather than folded in at request time. That is deliberate: the
   * radius living outside `searchParams` is exactly what let paging, sorting and the
   * CSV export each forget it. Nothing has to remember this one.
   *
   * The width is local state as well as a param because the select needs something to
   * show while the box is empty — and while it is empty the param must be absent, not
   * merely ignored. A tolerance with no frequency is a 422.
   */
  const [frequencyText, setFrequencyText] = useState('')
  /**
   * Whether a blanket band authorisation counts as being on the frequency.
   *
   * Held here beside the tolerance because it is the same kind of thing — how
   * wide a net the frequency question casts — and mirrored into `searchParams`
   * only while there is a frequency to qualify, exactly as the tolerance is.
   */
  const [includeBandGrants, setIncludeBandGrants] = useState(false)
  const [frequencyTolerance, setFrequencyTolerance] =
    useState<number>(DEFAULT_FREQUENCY_TOLERANCE_KHZ)

  /** Show the frequency a restored search carries — from the cache, or a saved one.

      Declared up here with the state it sets, because the session-cache effect below
      calls it and reads better next to what it touches than 150 lines further down. */
  const adoptFrequency = (params: LicenseSearchParams) => {
    setFrequencyText(params.frequency_mhz != null ? String(params.frequency_mhz) : '')
    setFrequencyTolerance(params.frequency_tolerance_khz ?? DEFAULT_FREQUENCY_TOLERANCE_KHZ)
    setIncludeBandGrants(Boolean(params.include_band_grants))
  }

  // Advanced filters state
  const [advancedOpen, setAdvancedOpen] = useState(false)

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([])
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saveError, setSaveError] = useState('')
  const [savesDropdownOpen, setSavesDropdownOpen] = useState(false)

  // Add as Lead modal state
  const [leadModal, setLeadModal] = useState<{ license: FCCLicense } | null>(null)
  const [leadPriority, setLeadPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium')
  const [leadNote, setLeadNote] = useState('')
  const [leadActionLoading, setLeadActionLoading] = useState(false)
  const [leadActionError, setLeadActionError] = useState('')
  // Per-row action feedback: usi → 'lead' | 'client' | 'loading' | 'error'
  const [rowActionStatus, setRowActionStatus] = useState<Record<number, string>>({})
  // Per-row entity IDs: usi → lead.id or client.id (for building links)
  const [rowActionIds, setRowActionIds] = useState<Record<number, number>>({})

  // Add as Client modal state (link to existing client or create new)
  const [clientModal, setClientModal] = useState<{
    license: FCCLicense
    matches: ClientMatchResult[]
  } | null>(null)
  const [clientModalSelectedId, setClientModalSelectedId] = useState<number | 'new'>('new')
  const [clientModalLoading, setClientModalLoading] = useState(false)
  const [clientModalError, setClientModalError] = useState('')

  // Filter option lists, from the imported data rather than hard-coded here.
  // Null until loaded; the selects fall back to whatever is already selected so
  // a restored search still shows its own filters if this request fails.
  const [facets, setFacets] = useState<LicenseFacets | null>(null)

  // Bulk add leads modal
  const [bulkLeadModal, setBulkLeadModal] = useState(false)
  /** Default ON: including suppressed licenses in a working queue is the
      mistake this whole sprint is about, so it has to be chosen deliberately. */
  const [bulkSkipSuppressed, setBulkSkipSuppressed] = useState(true)
  const [bulkPriority, setBulkPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium')
  const [bulkNote, setBulkNote] = useState('')
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkResult, setBulkResult] = useState<{ added: number; skipped: number } | null>(null)

  /**
   * Run a search and put the whole page in the state that response describes.
   *
   * **The only path that fetches results.** There used to be four — the form submit,
   * a near-duplicate inside its auto-geocode branch, page change and sort — each with
   * its own copy of the response handling, and they had drifted: page change sent the
   * raw params and silently dropped lat/lon/radius, so paging a radius search returned
   * the whole country from page 2 onwards. A box that searches while you type would
   * have made a fifth copy, so they are one.
   */
  /**
   * Put one set of annotations into state.
   *
   * Shared by the search response and the batch refresh so the five badge states are
   * always written together. They used to be set only inside `runSearch`, which is
   * why a page restored from the cache showed none of them.
   */
  const applyAnnotations = useCallback((a: LicenseAnnotations) => {
    setDoNotRenewUsis(new Set(a.do_not_renew_usis ?? []))
    setDncCallSigns(new Set(a.dnc_call_signs ?? []))
    setPendingRenewalCallSigns(new Set(a.pending_renewal_call_signs ?? []))
    const { status, ids } = buildCrmRowStatus(a)
    setRowActionStatus(status)
    setRowActionIds(ids)
  }, [])

  const runSearch = useCallback(async (params: LicenseSearchParams) => {
    setError('')
    setLoading(true)
    try {
      const response = await searchLicenses(params)
      const newResults = response.licenses
      const newTotal = response.total
      const newPages = Math.ceil(newTotal / (params.limit ?? 25))
      setResults(newResults)
      setTotalResults(newTotal)
      setTotalPages(newPages)
      applyAnnotations(response)
      const newFrequencies = response.matched_frequencies ?? {}
      const newBands = response.matched_bands ?? {}
      const newDistances = response.matched_distance_miles ?? {}
      const newSites = response.matched_sites ?? {}
      setMatchedFrequencies(newFrequencies)
      setMatchedBands(newBands)
      setMatchedDistances(newDistances)
      setMatchedSites(newSites)
      // Persist so navigating to a detail page and pressing Back restores the results.
      try {
        const cache: SearchCache = {
          searchParams: params,
          searchTerm, searchMode,
          datePreset, grantPreset,
          geoQuery, geoResolved, geoLat, geoLon, geoRadius,
          advancedOpen, results: newResults, totalResults: newTotal, totalPages: newPages,
          matchedFrequencies: newFrequencies, matchedBands: newBands,
          matchedDistances: newDistances, matchedSites: newSites,
        }
        sessionStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify(cache))
      } catch {
        // sessionStorage may be unavailable in some privacy modes
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      setLoading(false)
    }
  }, [searchTerm, searchMode, datePreset, grantPreset, geoQuery, geoResolved,
      geoLat, geoLon, geoRadius, advancedOpen, applyAnnotations])

  /**
   * Search as the user types.
   *
   * Guarded by the **last term actually searched**, not by a "have I mounted" flag.
   * StrictMode invokes effects twice in development, which defeats a mount flag: the
   * first invocation consumes it and the second runs a real search with an empty
   * term — an unfiltered query plus its count over every license in the database,
   * on every visit to the page. Comparing terms is idempotent, so a second invocation
   * with the same value does nothing.
   *
   * `null` means "nothing searched yet": the first settled value is adopted rather
   * than searched, so arriving at the page runs no query, and restoring a cached
   * search does not re-run the query whose results were just restored.
   *
   * Below the minimum the term is ignored rather than searched — two characters match
   * a sixth of the table. Clearing the box does search again, so deleting a term
   * returns to the unfiltered list instead of leaving stale results on screen.
   */
  const lastSearchedTerm = useRef<string | null>(null)
  useEffect(() => {
    const term = debouncedTerm.trim()
    if (lastSearchedTerm.current === null) {
      lastSearchedTerm.current = term
      return
    }
    if (lastSearchedTerm.current === term) return
    if (term && term.length < MIN_SEARCH_LENGTH) return
    lastSearchedTerm.current = term
    setSearchMode('starts')
    setSearchParams((prev) => ({ ...prev, page: 1 }))
    runSearch(buildActiveParams({
      search: term || undefined, search_mode: 'starts', page: 1,
    }))
    // buildActiveParams reads the current form by closure; keying this on the term is
    // the point — changing a dropdown should not fire a search on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTerm])

  // Restore previous search from session storage on mount, unless navigated here with preset params
  useEffect(() => {
    const preset = (location.state as { searchPreset?: LicenseSearchParams } | null)?.searchPreset
    if (preset) {
      const params = adoptDerivedPresets(resolvePresetDates({ page: 1, limit: 25, ...preset }))
      setSearchParams(params)
      setDatePreset(presetFor(
        EXPIRY_RANGE_PRESETS, params.expiration_preset,
        params.expiration_start, params.expiration_end,
      ))
      setGrantPreset(presetFor(
        GRANT_RANGE_PRESETS, params.grant_date_preset,
        params.grant_date_start, params.grant_date_end,
      ))
      runSearch(params)
      return
    }
    try {
      const raw = sessionStorage.getItem(SEARCH_CACHE_KEY)
      if (raw) {
        const cache: SearchCache = JSON.parse(raw)
        // A cache written yesterday names a preset whose dates have since moved on,
        // so re-resolve rather than restoring the stale pair. A cache from before
        // presets were persisted has no key at all — derivePreset guesses one from
        // the dates, which is right for a cache that is at most a session old.
        const restored = adoptDerivedPresets(resolvePresetDates(cache.searchParams ?? {}))
        setSearchParams(restored)
        setDatePreset(presetFor(
          EXPIRY_RANGE_PRESETS, restored.expiration_preset,
          restored.expiration_start, restored.expiration_end,
        ))
        setGrantPreset(presetFor(
          GRANT_RANGE_PRESETS, restored.grant_date_preset,
          restored.grant_date_start, restored.grant_date_end,
        ))
        setSearchTerm(cache.searchTerm ?? '')
        setSearchMode(cache.searchMode ?? 'starts')
        // Adopt it as already-searched: these results ARE that search.
        lastSearchedTerm.current = (cache.searchTerm ?? '').trim()
        setGeoQuery(cache.geoQuery ?? '')
        setGeoResolved(cache.geoResolved ?? '')
        setGeoLat(cache.geoLat)
        setGeoLon(cache.geoLon)
        setGeoRadius(cache.geoRadius)
        adoptFrequency(restored)
        setAdvancedOpen(cache.advancedOpen)
        if (cache.results.length > 0) {
          setResults(cache.results)
          setTotalResults(cache.totalResults)
          setTotalPages(cache.totalPages)
          // Written by older builds without these, hence the fallbacks.
          setMatchedFrequencies(cache.matchedFrequencies ?? {})
          setMatchedBands(cache.matchedBands ?? {})
          setMatchedDistances(cache.matchedDistances ?? {})
          setMatchedSites(cache.matchedSites ?? {})
          // The badges are NOT cached, on purpose: stale is exactly what they were.
          // Mark a license Do Not Contact, press Back, and the cached value would
          // still say otherwise. So the rows paint immediately from the cache and
          // the badges are re-read live. Same path covers the Search menu item.
          //
          // On failure the badges stay ABSENT rather than wrong. An absent badge is
          // recoverable — the next search fills it in — and a wrong one is not: it
          // is what let a Do Not Contact license look callable.
          const usis = cache.results
            .map((lic) => lic.unique_system_identifier)
            .filter((usi): usi is number => typeof usi === 'number')
          if (usis.length > 0) {
            void getLicenseAnnotations(usis)
              .then(applyAnnotations)
              .catch(() => { /* leave badges absent; see above */ })
          }
        }
      }
    } catch {
      // Ignore parse errors — stale or corrupt cache
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    listSavedSearches()
      .then((r) => setSavedSearches(r.searches))
      .catch(() => {}) // Non-critical; silently ignore
  }, [])

  useEffect(() => {
    // Non-critical: if this fails the selects fall back to their current value
    // plus "All …", which still lets an existing search be re-run.
    getLicenseFacets()
      .then(setFacets)
      .catch(() => {})
  }, [])

  const handleSaveSearch = async () => {
    if (!saveName.trim()) {
      setSaveError('Please enter a name')
      return
    }
    setSaveError('')
    try {
      const saved = await createSavedSearch(saveName.trim(), buildActiveParams())
      setSavedSearches([saved, ...savedSearches])
      setSaveModalOpen(false)
      setSaveName('')
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save search')
    }
  }

  const handleDeleteSavedSearch = async (id: number) => {
    try {
      await deleteSavedSearch(id)
      setSavedSearches(savedSearches.filter((s) => s.id !== id))
    } catch {
      // ignore
    }
  }

  /**
   * Choosing a named range fills both date inputs and records the key.
   *
   * The key is the part that matters: it is what gets saved and what the server
   * re-resolves on every run. The dates are filled in so the range is visible and
   * so an older client that ignores the key still gets a sensible window.
   * "Custom range…" keeps whatever dates are there and drops the key.
   */
  const applyDatePreset = (value: string) => {
    setDatePreset(value)
    if (value === CUSTOM_RANGE) {
      setSearchParams({ ...searchParams, expiration_preset: undefined })
      return
    }
    const { start, end } = EXPIRY_RANGE_PRESETS.find((p) => p.value === value)?.range() ?? {}
    setSearchParams({
      ...searchParams,
      expiration_preset: value || undefined,
      expiration_start: start,
      expiration_end: end,
      // The other half of the rule in PrimaryFilters' onChange: an expiration window
      // and a construction window rarely overlap (construction deadlines fall about a
      // year after the grant, expiry ten), so choosing a named range clears the
      // construction filter. "Any" and "Custom range" leave it alone.
      ...(value ? { construction_within_days: undefined } : {}),
    })
  }

  /**
   * How many Advanced filters are set.
   *
   * Shown on the collapsed toggle, because Advanced starts closed: a filter that is
   * applied but out of sight is one nobody can account for, which is exactly how a
   * stale call sign emptied every search on production. The count is the cheap,
   * permanent version of that lesson — you may not know *which* filter is narrowing
   * your results, but you can always see that something is.
   */
  const advancedActiveCount = useMemo(() => {
    const fields: (keyof LicenseSearchParams)[] = [
      'call_sign', 'licensee_name', 'contact_name', 'email', 'phone', 'frn',
      'city', 'zip_code', 'grant_date_start', 'grant_date_end',
      // The frequency counts; its tolerance does not. They are one question, and
      // showing "2 active" for one filled-in box would make the badge harder to
      // reconcile with the form than no badge at all.
      'frequency_mhz',
    ]
    const count = fields.filter((f) => {
      const value = searchParams[f]
      return typeof value === 'string' ? value.trim() !== '' : value != null
    }).length + (geoLat ? 1 : 0)
    // A NON-DEFAULT correlation counts, for exactly the reason in the docstring
    // above: it changes what comes back and it lives behind a collapsed panel. The
    // default does not, or every frequency+radius search would claim an extra
    // filter nobody set.
    const correlation = searchParams.frequency_site_correlation
    const station = searchParams.station_class
    const dnc = searchParams.dnc_status
    return count
      + (correlation && correlation !== 'same_site' ? 1 : 0)
      + (station && station !== 'any' ? 1 : 0)
      + (dnc && dnc !== 'any' ? 1 : 0)
  }, [searchParams, geoLat])

  /**
   * Whether to show the Match column at all.
   *
   * Keyed off the ANNOTATIONS the server sent, not off the filter fields, so the
   * column appears exactly when there is something in it. A search whose filter
   * matched nothing on this page shows no empty column, and — the case that
   * matters — a restored session cache from a frequency search still shows it.
   */
  const showMatchColumn = useMemo(
    () => Object.keys(matchedFrequencies).length > 0
       || Object.keys(matchedBands).length > 0
       || Object.keys(matchedDistances).length > 0
       || Object.keys(matchedSites).length > 0,
    [matchedFrequencies, matchedBands, matchedDistances, matchedSites],
  )

  /**
   * The label of the expiration window currently in force, or '' when it is not
   * narrowing anything.
   *
   * The page opens on "the next 120 days" because the usual question here is a
   * renewal-desk one. That default is silent, and it ANDs with everything else —
   * which is how a frequency search for 152.480 came back with nothing while the
   * database held 4,523 assignments on that exact frequency across 496 licenses.
   * Precisely one of them expires in the next 120 days. Nothing on screen said
   * so, and the empty state said "set a filter" to somebody who had just set one.
   *
   * Not fixed by changing the default: for the renewal desk the default is
   * right. Fixed by saying what it is, at the only moment anyone needs to know.
   */
  const narrowingExpiry = useMemo(() => {
    if (!searchParams.expiration_preset && !searchParams.expiration_start
        && !searchParams.expiration_end) return ''
    if (searchParams.expiration_preset) {
      return EXPIRY_RANGE_PRESETS.find(
        (p) => p.value === searchParams.expiration_preset,
      )?.label ?? ''
    }
    return 'the custom expiration range'
  }, [searchParams.expiration_preset, searchParams.expiration_start,
      searchParams.expiration_end])

  /** Whether anything at all is filtering, so the empty state can tell the truth. */
  const anyFilterSet = useMemo(
    () => Boolean(
      searchTerm.trim() || narrowingExpiry || advancedActiveCount > 0
      || searchParams.state || searchParams.service_code || searchParams.status,
    ),
    [searchTerm, narrowingExpiry, advancedActiveCount, searchParams.state,
     searchParams.service_code, searchParams.status],
  )

  /** Drop the expiration window and re-run — the escape hatch from the trap above.
   *
   * Runs the search itself rather than only clearing the field. Changing a
   * dropdown deliberately does NOT fire a search on this page, and an escape
   * hatch that leaves you looking at the same empty screen is not one. The new
   * window is passed as an explicit override because `setSearchParams` has not
   * landed yet when `buildActiveParams` reads state.
   */
  const searchAllDates = () => {
    const cleared = {
      page: 1,
      expiration_preset: undefined,
      expiration_start: undefined,
      expiration_end: undefined,
    }
    setDatePreset('')
    setSearchParams((prev) => ({ ...prev, ...cleared }))
    runSearch(buildActiveParams({ ...cleared, search_mode: searchMode }))
  }

  /**
   * Start over: empty the box, drop every filter, forget the cached results.
   *
   * Deliberately resets the geo state and the session cache too. "Clear" that leaves
   * an invisible radius or a stale cached search behind is the same trap in a
   * different coat.
   */
  const clearAll = () => {
    setSearchTerm('')
    setSearchMode('starts')
    setSearchParams(initialSearchParams(searchParams.limit ?? 25))
    setDatePreset(DEFAULT_EXPIRY_PRESET)
    setGrantPreset('')
    setGeoQuery(''); setGeoResolved(''); setGeoLat(''); setGeoLon(''); setGeoRadius(25)
    setGeoError('')
    setFrequencyText(''); setFrequencyTolerance(DEFAULT_FREQUENCY_TOLERANCE_KHZ)
    setIncludeBandGrants(false)
    setResults([])
    setTotalResults(0)
    setTotalPages(0)
    setError('')
    lastSearchedTerm.current = ''
    try {
      sessionStorage.removeItem(SEARCH_CACHE_KEY)
    } catch {
      // sessionStorage may be unavailable in some privacy modes
    }
  }

  /**
   * Typing in the frequency box, and choosing how close a match counts.
   *
   * Both write through to `searchParams` immediately, and both drop the pair together
   * when the box does not hold a usable number — an unparseable frequency must leave
   * *no* frequency filter behind, not a stale one from two keystrokes ago.
   */
  const applyFrequency = (raw: string) => {
    setFrequencyText(raw)
    const parsed = parseFrequency(raw)
    patchParams(parsed === null
      ? {
          frequency_mhz: undefined,
          frequency_tolerance_khz: undefined,
          include_band_grants: undefined,
        }
      : {
          frequency_mhz: parsed,
          frequency_tolerance_khz: frequencyTolerance,
          include_band_grants: includeBandGrants || undefined,
        })
  }

  const applyFrequencyTolerance = (khz: number) => {
    setFrequencyTolerance(khz)
    if (parseFrequency(frequencyText) !== null) {
      patchParams({ frequency_tolerance_khz: khz })
    }
  }

  const applyIncludeBandGrants = (on: boolean) => {
    setIncludeBandGrants(on)
    if (parseFrequency(frequencyText) !== null) {
      // `undefined` rather than `false` so an ordinary search sends no such key
      // at all — it keeps saved searches and the audit blob free of a parameter
      // nobody set, and the server's default is already false.
      patchParams({ include_band_grants: on || undefined })
    }
  }

  /** As applyDatePreset, for the grant-date range in Advanced Filters. */
  const applyGrantPreset = (value: string) => {
    setGrantPreset(value)
    if (value === CUSTOM_RANGE) {
      setSearchParams({ ...searchParams, grant_date_preset: undefined })
      return
    }
    const { start, end } = GRANT_RANGE_PRESETS.find((p) => p.value === value)?.range() ?? {}
    setSearchParams({
      ...searchParams,
      grant_date_preset: value || undefined,
      grant_date_start: start,
      grant_date_end: end,
    })
  }

  /**
   * Switch a named range to "Custom range…", keeping the dates it produced.
   *
   * The dates are disabled while a preset is active, so this is how you get from
   * "the next 90 days" to editing its endpoints: it turns the derived range into an
   * explicit one you own, which then stays put instead of moving with the calendar.
   */
  const unlockDates = (which: 'expiry' | 'grant') => {
    if (which === 'expiry') applyDatePreset(CUSTOM_RANGE)
    else applyGrantPreset(CUSTOM_RANGE)
  }

  /**
   * Editing a date by hand re-derives the preset, so the select keeps telling the
   * truth: it snaps to a named range if the dates match one and falls back to
   * "Custom range…" (or "Any expiration date", once both are cleared) if not.
   *
   * Only reachable once the dates are unlocked, i.e. the range is already custom —
   * but a date that happens to land exactly on a preset's window is still shown as
   * that preset, and stored as one, so it keeps moving with the calendar.
   */
  const setExpiryBound = (bound: 'expiration_start' | 'expiration_end', value: string) => {
    const next = { ...searchParams, [bound]: value || undefined }
    const derived = derivePreset(EXPIRY_RANGE_PRESETS, next.expiration_start, next.expiration_end)
    next.expiration_preset = isRelativePreset(derived) ? derived : undefined
    setSearchParams(next)
    setDatePreset(derived)
  }

  /** As setExpiryBound, for the grant-date range. */
  const setGrantBound = (bound: 'grant_date_start' | 'grant_date_end', value: string) => {
    const next = { ...searchParams, [bound]: value || undefined }
    const derived = derivePreset(GRANT_RANGE_PRESETS, next.grant_date_start, next.grant_date_end)
    next.grant_date_preset = isRelativePreset(derived) ? derived : undefined
    setSearchParams(next)
    setGrantPreset(derived)
  }

  // ISO dates compare correctly as strings. A backwards range is the trap the old
  // two-select design set for people, so say so rather than return zero results.
  const expiryRangeIsEmpty = Boolean(
    searchParams.expiration_start &&
      searchParams.expiration_end &&
      searchParams.expiration_start > searchParams.expiration_end,
  )
  const grantRangeIsEmpty = Boolean(
    searchParams.grant_date_start &&
      searchParams.grant_date_end &&
      searchParams.grant_date_start > searchParams.grant_date_end,
  )

  const handleToggleWatch = async (s: SavedSearch) => {
    try {
      const updated = await toggleSavedSearchWatch(s.id, !s.watch_enabled)
      setSavedSearches(savedSearches.map((x) => (x.id === updated.id ? updated : x)))
    } catch {
      // ignore — non-critical
    }
  }

  const handleToggleFeedsLeads = async (s: SavedSearch) => {
    try {
      const updated = await toggleSavedSearchFeedsLeads(s.id, !s.feeds_leads)
      setSavedSearches(savedSearches.map((x) => (x.id === updated.id ? updated : x)))
    } catch {
      // ignore — non-critical
    }
  }

  const handleLoadSavedSearch = (saved: SavedSearch) => {
    // Re-resolve first: a search saved as "the next 90 days" stored the key plus the
    // dates it meant that day, and it is the key that is authoritative. The server
    // does the same when it runs the search; this keeps the form showing the window
    // that will actually be searched, not the one that was current when it was saved.
    //
    // Deliberately NOT adoptDerivedPresets() — this is the one path where the params
    // already say what they mean. A stored fixed range that happens to coincide with
    // a preset's window today is still a fixed range, and must not be re-guessed into
    // one that moves. Only freshly-arrived dates get the benefit of the doubt.
    const params = resolvePresetDates({
      ...saved.search_params, page: 1, limit: searchParams.limit ?? 25,
    })
    setSearchParams(params)
    // The box and its mode are part of what the search means, so both come back. The
    // mode especially: a search saved as "match anywhere" that reloaded as "match from
    // the start" would quietly show a narrower set than the one that was saved.
    setSearchTerm(params.search ?? '')
    setSearchMode(params.search_mode ?? 'starts')
    setDatePreset(presetFor(
      EXPIRY_RANGE_PRESETS, params.expiration_preset,
      params.expiration_start, params.expiration_end,
    ))
    setGrantPreset(presetFor(
      GRANT_RANGE_PRESETS, params.grant_date_preset,
      params.grant_date_start, params.grant_date_end,
    ))
    adoptFrequency(params)
    // Restore geo state if saved
    if (saved.search_params.lat && saved.search_params.lon) {
      setGeoLat(String(saved.search_params.lat))
      setGeoLon(String(saved.search_params.lon))
      setGeoRadius(saved.search_params.radius_miles ?? 25)
      setGeoQuery('')
      setGeoResolved(`${parseFloat(String(saved.search_params.lat)).toFixed(4)}°, ${parseFloat(String(saved.search_params.lon)).toFixed(4)}°`)
    } else {
      setGeoLat(''); setGeoLon(''); setGeoQuery(''); setGeoResolved('')
    }
    setSavesDropdownOpen(false)
  }

  /**
   * The params a search should actually run with: what is on the form, plus the
   * radius, which lives in its own geo* state rather than in searchParams.
   *
   * `overrides` exists so paging and sorting cannot take the shortcut they used to —
   * spreading `searchParams` directly and losing the radius on the way.
   */
  const buildActiveParams = (
    overrides: Partial<LicenseSearchParams> = {},
  ): LicenseSearchParams => {
    const term = searchTerm.trim()
    const params: LicenseSearchParams = {
      ...searchParams,
      // The box lives in its own state (it drives a debounced request), so it is
      // folded in here rather than in each caller — which is exactly the mistake the
      // radius made: it was merged in some paths and forgotten in others.
      search: term || undefined,
      search_mode: searchMode,
      ...overrides,
    }
    if (geoLat && geoLon) {
      params.lat = parseFloat(geoLat)
      params.lon = parseFloat(geoLon)
      params.radius_miles = geoRadius
    }
    return params
  }

  /**
   * A radius search is ALWAYS ordered by distance — `licenses.py:620` ignores `sort_by`
   * outright when lat/lon/radius are present, because distance is the answer to the
   * question being asked. The headers used to stay clickable and paint an active arrow on
   * whatever you clicked, so the table claimed an ordering the backend had not applied.
   * Mirrors the backend's own `geo_search` condition (`licenses.py:329`).
   */
  const isRadiusSearch = Boolean(geoLat && geoLon)

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser. Enter a location manually.')
      return
    }
    setGeoLoading(true)
    setGeoError('')
    setGeoQuery('')
    setGeoResolved('')
    setGeoLat('')
    setGeoLon('')
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6)
        const lon = position.coords.longitude.toFixed(6)
        setGeoLat(lat)
        setGeoLon(lon)
        setGeoResolved(`My location (${parseFloat(lat).toFixed(4)}°, ${parseFloat(lon).toFixed(4)}°)`)
        setSearchParams(prev => ({ ...prev, state: '' }))
        setGeoLoading(false)
      },
      (err) => {
        const messages: Record<number, string> = {
          1: 'Location access was denied. Check your browser\'s site permissions and try again.',
          2: 'Your location is currently unavailable. Try entering a location manually.',
          3: 'Location request timed out. Try entering a location manually.',
        }
        setGeoError(messages[err.code] ?? 'Unable to retrieve your location. Try entering it manually.')
        setGeoLoading(false)
      },
      { timeout: 10_000, maximumAge: 300_000, enableHighAccuracy: false }
    )
  }

  async function resolveGeoQuery() {
    if (!geoQuery.trim()) return
    setGeoLoading(true)
    setGeoError('')
    try {
      const result = await geocodeQuery(geoQuery.trim())
      if (!result) {
        setGeoError('Location not found. Try a city name, ZIP code, or full address.')
        setGeoLat('')
        setGeoLon('')
        setGeoResolved('')
      } else {
        setGeoLat(result.lat)
        setGeoLon(result.lon)
        setGeoResolved(result.displayName)
        // Clear the state filter — it would conflict with a location-based search
        setSearchParams(prev => ({ ...prev, state: '' }))
      }
    } catch {
      setGeoError('Geocoding failed. Check your connection and try again.')
    } finally {
      setGeoLoading(false)
    }
  }

  /**
   * The Search button, and Enter in the box: run the thorough search.
   *
   * Typing already runs the quick one, so pressing the button means "look harder" —
   * `anywhere` matches inside words, at roughly seconds rather than milliseconds. It
   * stays in that mode until the term changes, so the note beside the results always
   * describes what is on screen.
   */
  const handleSearch = async (e?: FormEvent) => {
    e?.preventDefault()
    setError('')

    // A location typed but never resolved: geocode it first, then search with the
    // coordinates it produced. This used to run a second, copied search block.
    let geo: Partial<LicenseSearchParams> = {}
    if (geoQuery.trim() && !geoLat) {
      setGeoLoading(true)
      setGeoError('')
      try {
        const result = await geocodeQuery(geoQuery.trim())
        if (!result) {
          setGeoError('Location not found. Try a city name, ZIP code, or full address.')
          return
        }
        setGeoLat(result.lat)
        setGeoLon(result.lon)
        setGeoResolved(result.displayName)
        geo = {
          lat: parseFloat(result.lat),
          lon: parseFloat(result.lon),
          radius_miles: geoRadius,
        }
      } catch (err) {
        setGeoError(err instanceof Error ? err.message : 'Geocoding failed')
        return
      } finally {
        setGeoLoading(false)
      }
    }

    setSearchMode('anywhere')
    await runSearch(buildActiveParams({ ...geo, search_mode: 'anywhere', page: 1 }))
  }

  const handleExport = async () => {
    setError('')
    try {
      // buildActiveParams(), not searchParams — the radius lives in the geo* state and
      // is folded in there. Exporting the raw params silently dropped it, so "Export"
      // after a near-a-location search downloaded every license that matched the rest.
      const blob = await exportLicensesToCSV(buildActiveParams())
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `fcc-licenses-${format(new Date(), 'yyyy-MM-dd')}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }))
    runSearch(buildActiveParams({ page: newPage, search_mode: searchMode }))
  }

  /** Rows per page lives in `searchParams.limit`, so it is cached and saved with the search. */
  const handlePageSizeChange = (size: number) => {
    setSearchParams((prev) => ({ ...prev, limit: size, page: 1 }))
    runSearch(buildActiveParams({ limit: size, page: 1, search_mode: searchMode }))
  }

  /**
   * NOT `useTableSort`, on purpose — this is the one of the five sort call sites the hook
   * does not fit, and that is a fact about this page rather than a gap in the hook.
   *
   * Everywhere else the sort is two pieces of local state. Here it is two fields of
   * `searchParams`, which is persisted to sessionStorage, restored from a saved search,
   * and handed wholesale to `runSearch`. Adopting the hook would mean the sort lived in
   * the hook's state *and* in `searchParams`, i.e. two sources of truth for one value —
   * and the first time they disagreed the table would show one ordering while the export
   * used another. The toggle rule and the `page: 1` reset are duplicated here instead,
   * which is the cheaper of the two problems.
   */
  const handleSort = (col: string) => {
    const newDir: 'asc' | 'desc' =
      searchParams.sort_by === col && searchParams.sort_dir === 'asc' ? 'desc' : 'asc'
    setSearchParams((prev) => ({ ...prev, sort_by: col, sort_dir: newDir, page: 1 }))
    runSearch(buildActiveParams({
      sort_by: col, sort_dir: newDir, page: 1, search_mode: searchMode,
    }))
  }

  /**
   * How this search reads to somebody who did not run it.
   *
   * Stored on every lead created by a bulk import and shown on the lead detail page,
   * so it has to describe the search that actually ran. It named the old Call
   * Sign / Name / Email / Phone inputs; those are one box now, and a term matched
   * inside words finds a wider set than the same term matched from the start, so the
   * description says which.
   */
  function buildSearchDescription(): string {
    const parts: string[] = []
    const term = searchTerm.trim()
    if (term) {
      const how = searchMode === 'anywhere' ? 'matching anywhere' : 'matching from the start'
      parts.push(`“${term}” (${how})`)
    }
    if (searchParams.call_sign) parts.push(`Call sign: ${searchParams.call_sign}`)
    if (searchParams.licensee_name) parts.push(`Licensee: ${searchParams.licensee_name}`)
    if (searchParams.contact_name) parts.push(`Contact: ${searchParams.contact_name}`)
    if (searchParams.email) parts.push(`Email: ${searchParams.email}`)
    if (searchParams.phone) parts.push(`Phone: ${searchParams.phone}`)
    if (searchParams.frn) parts.push(`FRN: ${searchParams.frn}`)
    if (searchParams.frequency_mhz != null) {
      // The width belongs in the sentence: "154.57 MHz" alone does not say whether
      // one channel or a whole band was asked for, and this text is the only account
      // of the search a lead created from it will ever carry.
      const khz = searchParams.frequency_tolerance_khz ?? DEFAULT_FREQUENCY_TOLERANCE_KHZ
      parts.push(`Frequency: ${searchParams.frequency_mhz} MHz ± ${khz} kHz`)
    }
    if (searchParams.state) parts.push(`State: ${searchParams.state}`)
    if (searchParams.service_code) parts.push(`Service: ${searchParams.service_code}`)
    if (searchParams.status) parts.push(`Status: ${LICENSE_STATUS[searchParams.status] || searchParams.status}`)
    const { expiration_start: from, expiration_end: to } = searchParams
    if (from && to) parts.push(`Expiring ${from} to ${to}`)
    else if (from) parts.push(`Expiring on or after ${from}`)
    else if (to) parts.push(`Expiring on or before ${to}`)
    return parts.length > 0 ? `Search: ${parts.join(', ')}` : 'Bulk import from search results'
  }

  async function handleAddAsLead() {
    if (!leadModal) return
    setLeadActionLoading(true)
    setLeadActionError('')
    try {
      const lead = await createTrackedLead({
        fcc_unique_system_identifier: leadModal.license.unique_system_identifier,
        priority: leadPriority,
        note: leadNote.trim() || undefined,
        source: 'manual',
      })
      setRowActionStatus((prev) => ({
        ...prev,
        [leadModal.license.unique_system_identifier]: 'lead',
      }))
      setRowActionIds((prev) => ({
        ...prev,
        [leadModal.license.unique_system_identifier]: lead.id,
      }))
      setLeadModal(null)
      setLeadNote('')
      setLeadPriority('medium')
    } catch (err) {
      setLeadActionError(err instanceof Error ? err.message : 'Failed to add lead')
    } finally {
      setLeadActionLoading(false)
    }
  }

  async function openClientModal(license: FCCLicense) {
    const usi = license.unique_system_identifier
    setClientModalError('')
    setClientModalLoading(true)
    try {
      const matches = await matchClients({ entity_name: license.entity?.entity_name ?? undefined })
      setClientModalLoading(false)
      if (matches.length === 0) {
        // No matches — create new client directly
        await doAddAsClient(license, 'new')
      } else {
        setClientModalSelectedId('new')
        setClientModal({ license, matches })
      }
    } catch {
      setClientModalLoading(false)
      setRowActionStatus((prev) => ({ ...prev, [usi]: 'error' }))
    }
  }

  async function doAddAsClient(license: FCCLicense, selectedId: number | 'new') {
    const usi = license.unique_system_identifier
    setRowActionStatus((prev) => ({ ...prev, [usi]: 'loading' }))
    setClientModalError('')
    try {
      let clientId: number
      if (selectedId === 'new') {
        const client = await convertLead(usi)
        clientId = client.id
      } else {
        const cl = await linkLicense(selectedId, usi)
        clientId = cl.client_id
      }
      setRowActionStatus((prev) => ({ ...prev, [usi]: 'client' }))
      setRowActionIds((prev) => ({ ...prev, [usi]: clientId }))
      setClientModal(null)
    } catch (err) {
      setRowActionStatus((prev) => { const s = { ...prev }; delete s[usi]; return s })
      setClientModalError(err instanceof Error ? err.message : 'Failed to add as client')
    }
  }

  /** Reflect a DncToggle result in the set of flagged call signs. */
  function applyDncChange(callSign: string, isDnc: boolean) {
    setDncCallSigns((prev) => {
      const s = new Set(prev)
      if (isDnc) s.add(callSign)
      else s.delete(callSign)
      return s
    })
  }

  /**
   * The rows "Add as Leads" would actually create something for.
   *
   * Excludes licenses already tracked as a lead, and — the bug this fixes —
   * licenses that belong to an existing client. A client is a customer we are
   * already serving, not a prospect. The count in the button used to be
   * results.length, which promised work the server would refuse to do.
   */
  const addableUsis = useMemo(
    () => results
      .map((l) => l.unique_system_identifier)
      .filter((usi) => !rowActionStatus[usi]),
    [results, rowActionStatus],
  )

  /**
   * Of the licenses we are about to add, which are on the Do Not Contact list.
   *
   * Bulk add never consulted the list at all, so "Add 40 as Leads" could quietly
   * pull suppressed licenses into the working queue — the exact route by which
   * somebody on Do Not Call ends up being rung.
   */
  const suppressedAddableUsis = useMemo(
    () => {
      const byUsi = new Map(results.map((l) => [l.unique_system_identifier, l.call_sign]))
      return addableUsis.filter((usi) => {
        const callSign = byUsi.get(usi)
        return !!callSign && dncCallSigns.has(callSign)
      })
    },
    [addableUsis, results, dncCallSigns],
  )

  async function handleBulkAddLeads() {
    setBulkLoading(true)
    setBulkResult(null)
    try {
      const usis = bulkSkipSuppressed
        ? addableUsis.filter((usi) => !suppressedAddableUsis.includes(usi))
        : addableUsis
      const result = await bulkCreateTrackedLeads({
        fcc_usis: usis,
        priority: bulkPriority,
        note: bulkNote.trim() || undefined,
        search_description: buildSearchDescription(),
      })
      setBulkResult({ added: result.added, skipped: result.skipped })
      // Mark only the rows we actually sent. Marking every result turned an
      // existing CLIENT's row into a "lead" badge in the UI even though the
      // server had correctly refused to create one.
      const updates: Record<number, string> = {}
      usis.forEach((usi) => { updates[usi] = 'lead' })
      setRowActionStatus((prev) => ({ ...prev, ...updates }))
    } catch {
      setBulkResult(null)
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <div>
      <PageHeading title="License Search" help={SEARCH_HELP} className="mb-6" />

      {/* The toolbar above the form: what you can load on the left, what you can do
          with the current search on the right. Save and Export moved up here on
          2026-08-15 — they act on a search rather than running one, so grouping them
          with Search and Clear at the bottom put four buttons of two different kinds
          in one row. This bar renders even with no saved searches, or "Save This
          Search" would be missing exactly when you are about to create the first. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          {savedSearches.length > 0 && (
            <>
              <span className="text-sm text-gray-600">Saved searches:</span>
            <span className="text-sm text-gray-600">Saved searches:</span>
            <div className="relative">
              <button
                type="button"
                onClick={() => setSavesDropdownOpen(!savesDropdownOpen)}
                className="btn btn-secondary text-sm"
              >
                Load a search ▾
              </button>
              {savesDropdownOpen && (
                <div className="absolute left-0 mt-1 w-72 bg-white border border-gray-200 rounded shadow-lg z-10">
                  {savedSearches.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                    >
                      <button
                        type="button"
                        onClick={() => handleLoadSavedSearch(s)}
                        className="text-sm text-left text-gray-800 flex-1 truncate"
                      >
                        {s.name}
                        {s.new_match_count > 0 && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {s.new_match_count}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleWatch(s)}
                        className={`ml-1 p-1 rounded flex-shrink-0 ${s.watch_enabled ? 'text-blue-500 hover:text-blue-700' : 'text-gray-300 hover:text-gray-500'}`}
                        title={s.watch_enabled ? 'Watching for new matches (click to disable)' : 'Enable new-match alerts for this search'}
                        aria-label={s.watch_enabled ? `Disable watch: ${s.name}` : `Enable watch: ${s.name}`}
                        aria-pressed={s.watch_enabled}
                      >
                        <BellIcon active={s.watch_enabled} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleFeedsLeads(s)}
                        className={`ml-1 px-1.5 py-0.5 text-xs rounded flex-shrink-0 border ${
                          s.feeds_leads
                            ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                            : 'border-transparent text-gray-300 hover:text-gray-500'
                        }`}
                        title={s.feeds_leads
                          ? "Feeding the team's auto-discovered leads (click to stop)"
                          : "Show this search's matches on the team's Leads page"}
                        aria-label={s.feeds_leads
                          ? `Stop feeding leads from: ${s.name}`
                          : `Feed leads from: ${s.name}`}
                        aria-pressed={s.feeds_leads}
                      >
                        Leads
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSavedSearch(s.id)}
                        className="ml-1 p-1 text-red-400 hover:text-red-600 rounded flex-shrink-0"
                        title="Delete saved search"
                        aria-label={`Delete saved search: ${s.name}`}
                      >
                        <XIcon />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setSaveModalOpen(true); setSaveError('') }}
            className="btn btn-secondary text-sm"
          >
            Save This Search
          </button>
          {results.length > 0 && (
            <button type="button" onClick={handleExport} className="btn btn-secondary text-sm">
              Export to CSV
            </button>
          )}
        </div>
      </div>

      {/* Search form */}
      <div className="card mb-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <PrimaryFilters
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            minSearchLength={MIN_SEARCH_LENGTH}
            params={searchParams}
            onChange={(patch) => {
              // A construction window with "expiring in the next 120 days" still on
              // would match almost nothing: these licenses are about a year old. A
              // relative range is cleared; a custom one the user typed is left alone.
              if (patch.construction_within_days && datePreset && datePreset !== CUSTOM_RANGE) {
                setDatePreset('')
                patchParams({
                  ...patch,
                  expiration_preset: undefined,
                  expiration_start: undefined,
                  expiration_end: undefined,
                })
                return
              }
              patchParams(patch)
            }}
            facets={facets}
            expiration={{
              presets: EXPIRY_RANGE_PRESETS,
              preset: datePreset,
              onPresetChange: applyDatePreset,
              start: searchParams.expiration_start,
              end: searchParams.expiration_end,
              onStartChange: (v) => setExpiryBound('expiration_start', v),
              onEndChange: (v) => setExpiryBound('expiration_end', v),
              onUnlock: () => unlockDates('expiry'),
              derived: isRelativePreset(datePreset),
              warning: expiryRangeIsEmpty
                ? '\u201cFrom\u201d is after \u201cTo\u201d, so this range cannot match any license.'
                : undefined,
            }}
          />

          {/* Advanced filters */}
          <div className="border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={() => setAdvancedOpen(!advancedOpen)}
              aria-expanded={advancedOpen}
              aria-controls="advanced-filters"
              className="tap gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform duration-150 ${advancedOpen ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              Advanced Filters
              {advancedActiveCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded font-medium">
                  {advancedActiveCount} active
                </span>
              )}
              {geoLat && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded font-medium">
                  Near location active
                </span>
              )}
            </button>

            <div id="advanced-filters">
            {advancedOpen && (
              <div className="mt-3 space-y-4">
                {/* License details — everything that is a property of the license
                    itself. Location lives in its own section below; the two used to
                    be interleaved in one grid, so "City" sat between "Granted
                    Before" and "FRN" while "Near Location" was somewhere else
                    entirely.

                    These six fields ask for ONE specific thing; the box above asks
                    for any of them at once. Both are worth having: the box is what
                    you want when you have a name and do not know whose it is, and
                    these are what you want when you know exactly — "Keith County,
                    filed by Action" is a question only two separate name fields can
                    ask. They also have to exist for a duller reason: a filter with
                    no control is one nobody can see or clear, which is precisely how
                    a stale call sign silently emptied every search on 2026-08-15. */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">License details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <PresetDateRange
                        idPrefix="grant_date"
                        label="Grant date"
                        presets={GRANT_RANGE_PRESETS}
                        preset={grantPreset}
                        onPresetChange={applyGrantPreset}
                        start={searchParams.grant_date_start}
                        end={searchParams.grant_date_end}
                        onStartChange={(v) => setGrantBound('grant_date_start', v)}
                        onEndChange={(v) => setGrantBound('grant_date_end', v)}
                        onUnlock={() => unlockDates('grant')}
                        derived={isRelativePreset(grantPreset)}
                        warning={grantRangeIsEmpty
                          ? '\u201cFrom\u201d is after \u201cTo\u201d, so this range cannot match any license.'
                          : undefined}
                      />
                    </div>

                    <div className="space-y-4">
                      <TextFilterField
                        id="call_sign" label="Call sign"
                        placeholder="e.g. WPXR456" maxLength={10}
                        value={searchParams.call_sign || ''}
                        onChange={patchParams}
                        transform={(raw) => raw.toUpperCase()}
                      />
                      <TextFilterField
                        id="frn" label="FRN"
                        placeholder="FCC Registration Number" maxLength={10}
                        value={searchParams.frn || ''}
                        onChange={patchParams}
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <TextFilterField
                      id="licensee_name" label="Licensee name"
                      placeholder="Company or person" maxLength={200}
                      value={searchParams.licensee_name || ''}
                      onChange={patchParams}
                    />
                    <TextFilterField
                      id="contact_name" label="Contact name"
                      hint="(filing agent)"
                      placeholder="Who files for them" maxLength={200}
                      value={searchParams.contact_name || ''}
                      onChange={patchParams}
                    />
                    <TextFilterField
                      id="email" label="Email" hint="(any party)"
                      placeholder="e.g. renewals@example.com" maxLength={50}
                      value={searchParams.email || ''}
                      onChange={patchParams}
                    />
                    <TextFilterField
                      id="phone" label="Phone" hint="(any party)"
                      placeholder="e.g. 970555 or 9705551234" maxLength={20}
                      value={searchParams.phone || ''}
                      onChange={patchParams}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Licensee name and Contact name narrow each other, so filling both finds
                    licenses held by the one and filed by the other.
                  </p>

                  {/* Frequency sits with the license's other properties rather than
                      under Location: it is what the license authorises, not where.
                      It gets its own row because it is two controls answering one
                      question, plus the sentence saying which window they add up
                      to — dropped into the four-column grid above, the number and
                      its width would have read as unrelated filters. */}
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <FrequencyField
                      value={frequencyText}
                      onValueChange={applyFrequency}
                      tolerance={frequencyTolerance}
                      onToleranceChange={applyFrequencyTolerance}
                      includeBandGrants={includeBandGrants}
                      onIncludeBandGrantsChange={applyIncludeBandGrants}
                    />

                    {/* Fixed vs mobile. Shown whenever a frequency is in play — unlike
                        the correlation below it needs no radius, because "is this a
                        repeater or somebody's handhelds" is a question about the
                        assignment, not about where it is. */}
                    {searchParams.frequency_mhz != null && (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <label htmlFor="station_class" className="label">
                          Kind of station
                        </label>
                        <select
                          id="station_class"
                          className="input max-w-md"
                          value={searchParams.station_class || 'any'}
                          onChange={(e) => patchParams({
                            station_class:
                              e.target.value as LicenseSearchParams['station_class'],
                          })}
                        >
                          <option value="any">Any station</option>
                          <option value="fixed">
                            Fixed transmitters — base stations and repeaters
                          </option>
                          <option value="mobile">
                            Mobile units — handhelds and vehicles
                          </option>
                        </select>
                        {(searchParams.station_class ?? 'any') !== 'any' && (
                          <p className="mt-1 text-xs text-gray-500">
                            Describes the assignment that matched, not the license: a
                            license whose match is mobile will not appear under Fixed
                            because of an unrelated assignment elsewhere. About 0.1% of
                            assignments carry no usable class and appear under
                            “Any station” only.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Only shown when BOTH a frequency and a radius are set, because
                        that is the only time it does anything — same reasoning as the
                        tolerance select being hidden until a frequency is typed. A
                        control that silently has no effect is its own kind of lie. */}
                    {searchParams.frequency_mhz != null && geoLat !== '' && (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <label htmlFor="site_correlation" className="label">
                          Frequency and location must match
                        </label>
                        <select
                          id="site_correlation"
                          className="input max-w-md"
                          value={searchParams.frequency_site_correlation || 'same_site'}
                          onChange={(e) => patchParams({
                            frequency_site_correlation:
                              e.target.value as LicenseSearchParams['frequency_site_correlation'],
                          })}
                        >
                          <option value="same_site">
                            The same transmitter site (recommended)
                          </option>
                          <option value="same_site_known">
                            The same site, and only sites we can locate
                          </option>
                          <option value="any">
                            Anywhere on the license — show cross-site matches
                          </option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          {(searchParams.frequency_site_correlation || 'same_site') === 'same_site' && (
                            <>Matches where the frequency is at a site inside the radius,
                            or at a site the FCC data cannot place. Keeps mobile and
                            area-wide authorisations, which is most of 800/900&nbsp;MHz.</>
                          )}
                          {searchParams.frequency_site_correlation === 'same_site_known' && (
                            <>Only assignments at a site with known coordinates. Cleanest
                            answer, but it drops every match the FCC data cannot place —
                            around 30% of frequency records, and nearly all of
                            800/900&nbsp;MHz.</>
                          )}
                          {searchParams.frequency_site_correlation === 'any' && (
                            <>A license matches if it has any site in the radius and holds
                            the frequency anywhere — they need not be the same site. The
                            Match column labels those rows with where the frequency
                            actually is.</>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Do Not Contact moved here from the main filters (Jesse, 2026-10-30):
                    it is a setting most searches leave alone, and it is counted on
                    the collapsed toggle like every other Advanced filter. */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Contact list</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <DncField params={searchParams} onChange={patchParams} facets={facets} />
                  </div>
                </div>

                {/* Location — both senses of it, together and each labelled: the
                    licensee's mailing address (city/zip) and where the radio
                    actually transmits from (Near Location). Keeping them apart is
                    what made them look like unrelated filters. */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="inline h-4 w-4 mr-1 text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Location
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="city" className="label">
                        City
                        <span className="ml-1 text-xs font-normal text-gray-400">(licensee address)</span>
                      </label>
                      <input
                        id="city"
                        type="text"
                        className="input"
                        placeholder="e.g. Denver"
                        maxLength={20}
                        value={searchParams.city || ''}
                        onChange={(e) => setSearchParams({ ...searchParams, city: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="zip_code" className="label">
                        Zip Code
                        <span className="ml-1 text-xs font-normal text-gray-400">(licensee address)</span>
                      </label>
                      <input
                        id="zip_code"
                        type="text"
                        className="input"
                        placeholder="e.g. 80202"
                        maxLength={9}
                        value={searchParams.zip_code || ''}
                        onChange={(e) =>
                          setSearchParams({ ...searchParams, zip_code: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {/* Near Location stays inside this section: it is the other sense
                      of "where" — the transmitter site rather than the mailing
                      address — and separating them is what made the two look
                      unrelated. */}
                  <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Near Location
                    <span className="ml-1 text-xs font-normal text-gray-400">(searches by transmitter location)</span>
                  </p>

                  {/* Location input row */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="City, ZIP code, address, or state…"
                      value={geoQuery}
                      onChange={(e) => {
                        setGeoQuery(e.target.value)
                        // Clear resolved coords when text changes so stale coords don't persist
                        if (geoLat) { setGeoLat(''); setGeoLon(''); setGeoResolved('') }
                        setGeoError('')
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); resolveGeoQuery() }
                      }}
                      aria-label="Location for radius search"
                    />
                    <button
                      type="button"
                      onClick={resolveGeoQuery}
                      disabled={geoLoading || !geoQuery.trim()}
                      className="btn btn-secondary text-sm shrink-0"
                      aria-label="Look up location"
                    >
                      {geoLoading && !geoResolved ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : 'Find'}
                    </button>
                    <button
                      type="button"
                      onClick={useMyLocation}
                      disabled={geoLoading}
                      title="Use my current location"
                      aria-label="Use my current location"
                      className="btn btn-secondary text-sm shrink-0 flex items-center gap-1"
                    >
                      {geoLoading && !geoQuery.trim() ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className="hidden sm:inline">My Location</span>
                    </button>
                  </div>

                  {/* Resolved confirmation */}
                  {geoResolved && geoLat && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-1.5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="flex-1 truncate">{geoResolved}</span>
                      <button
                        type="button"
                        aria-label="Clear location"
                        onClick={() => { setGeoQuery(''); setGeoLat(''); setGeoLon(''); setGeoResolved(''); setGeoError('') }}
                        className="text-green-600 hover:text-green-800 ml-1 shrink-0"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Error message */}
                  {geoError && (
                    <p className="mt-2 text-sm text-red-600" role="alert">{geoError}</p>
                  )}

                  {/* Radius slider — only shown when a location is resolved */}
                  {geoLat && (
                    <div className="mt-3">
                      <label className="label text-xs">
                        Radius: <span className="font-semibold">{geoRadius} miles</span>
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={200}
                        value={geoRadius}
                        onChange={(e) => setGeoRadius(parseInt(e.target.value))}
                        className="w-full"
                        aria-label={`Search radius: ${geoRadius} miles`}
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>1 mi</span>
                        <span>200 mi</span>
                      </div>
                    </div>
                  )}
                  </div>{/* /near-location */}
                </div>{/* /location */}
              </div>
            )}
            </div>{/* /advanced-filters */}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={clearAll}
              disabled={loading}
              className="btn btn-secondary"
              title="Empty the search box and every filter, and start again"
            >
              Clear all
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="alert alert-error mb-6">{error}</div>}

      {/* DNC toast notification */}
      {dncToastMsg && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white text-sm px-4 py-2.5 rounded shadow-lg" role="status">
          {dncToastMsg}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="card">
          <div className="mb-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">
                Search Results ({totalResults.toLocaleString()} total)
              </h2>
              {/* Which kind of match produced these rows. Without it, "why did that
                  license not come up?" has no visible answer — the quick search is
                  matching from the start of a word, and nothing says so once the
                  results have replaced the help text below the box. */}
              {searchTerm.trim() && (
                <p className="mt-0.5 text-xs text-gray-500">
                  {searchMode === 'anywhere' ? (
                    <>Matching “{searchTerm.trim()}” anywhere in the text.</>
                  ) : (
                    <>
                      Matching “{searchTerm.trim()}” from the start of a word.{' '}
                      <button
                        type="button"
                        onClick={() => handleSearch()}
                        className="py-0.5 text-primary-600 hover:text-primary-800 underline"
                      >
                        Search inside words too
                      </button>
                    </>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setBulkNote(buildSearchDescription())
                  setBulkPriority('medium')
                  setBulkResult(null)
                  setBulkLeadModal(true)
                }}
                disabled={addableUsis.length === 0}
                title={
                  addableUsis.length === 0
                    ? 'Every result on this page is already a lead or a client'
                    : undefined
                }
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add {addableUsis.length} as Leads
              </button>
              <span className="text-sm text-gray-600">
                Page {searchParams.page} of {totalPages}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader
                    sortKey="call_sign"
                    currentSortKey={searchParams.sort_by ?? "call_sign"}
                    currentSortOrder={searchParams.sort_dir ?? "asc"}
                    onSort={handleSort}
                    disabled={isRadiusSearch}
                    disabledReason="A radius search is always ordered by distance from the search point."
                  >
                    Call Sign
                  </SortableHeader>
                  <SortableHeader
                    sortKey="entity_name"
                    currentSortKey={searchParams.sort_by ?? "call_sign"}
                    currentSortOrder={searchParams.sort_dir ?? "asc"}
                    onSort={handleSort}
                    disabled={isRadiusSearch}
                    disabledReason="A radius search is always ordered by distance from the search point."
                  >
                    Licensee
                  </SortableHeader>
                  {/* Not sortable: it is a property of the match, not a column of
                      the license, so there is nothing in the database to ORDER BY.
                      A geo search is already sorted by this distance. */}
                  {showMatchColumn && <th>Match</th>}
                  <SortableHeader
                    sortKey="service_code"
                    currentSortKey={searchParams.sort_by ?? "call_sign"}
                    currentSortOrder={searchParams.sort_dir ?? "asc"}
                    onSort={handleSort}
                    disabled={isRadiusSearch}
                    disabledReason="A radius search is always ordered by distance from the search point."
                    className="hidden sm:table-cell"
                  >
                    Service
                  </SortableHeader>
                  <SortableHeader
                    sortKey="license_status"
                    currentSortKey={searchParams.sort_by ?? "call_sign"}
                    currentSortOrder={searchParams.sort_dir ?? "asc"}
                    onSort={handleSort}
                    disabled={isRadiusSearch}
                    disabledReason="A radius search is always ordered by distance from the search point."
                  >
                    Status
                  </SortableHeader>
                  <SortableHeader
                    sortKey="grant_date"
                    currentSortKey={searchParams.sort_by ?? "call_sign"}
                    currentSortOrder={searchParams.sort_dir ?? "asc"}
                    onSort={handleSort}
                    disabled={isRadiusSearch}
                    disabledReason="A radius search is always ordered by distance from the search point."
                    className="hidden md:table-cell"
                  >
                    Grant Date
                  </SortableHeader>
                  <SortableHeader
                    sortKey="expired_date"
                    currentSortKey={searchParams.sort_by ?? "call_sign"}
                    currentSortOrder={searchParams.sort_dir ?? "asc"}
                    onSort={handleSort}
                    disabled={isRadiusSearch}
                    disabledReason="A radius search is always ordered by distance from the search point."
                  >
                    Expiration
                  </SortableHeader>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((license) => (
                  <tr
                    key={license.id}
                    onClick={() => navigate(`/licenses/${license.call_sign}`)}
                    // A small badge in a dense table is easy to scan past, which is
                    // how somebody on Do Not Call nearly got rung again. The whole
                    // row is tinted so the flag is visible without reading it.
                    className={
                      dncCallSigns.has(license.call_sign)
                        ? 'bg-red-50/60 hover:bg-red-100/60 cursor-pointer'
                        : 'hover:bg-gray-50 cursor-pointer'
                    }
                  >
                    <td className="font-medium text-primary-600">
                      <Link to={`/licenses/${license.call_sign}`}>{license.call_sign}</Link>
                      {doNotRenewUsis.has(license.unique_system_identifier) && (
                        <span
                          title="A client has marked this license as Do Not Renew — notifications and lead generation are suppressed"
                          className="ml-1.5 text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded align-middle"
                        >
                          no renew
                        </span>
                      )}
                      {pendingRenewalCallSigns.has(license.call_sign) && (
                        <span
                          title="This licensee has already filed a renewal application with the FCC — not a renewal prospect"
                          className="ml-1.5 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded align-middle font-semibold"
                        >
                          Pending
                        </span>
                      )}
                      {dncCallSigns.has(license.call_sign) && <DncBadge className="ml-1.5" />}
                    </td>
                    <td className="max-w-[160px] truncate" title={license.entity?.entity_name || 'N/A'}>
                      {license.entity?.entity_name || 'N/A'}
                    </td>
                    {showMatchColumn && (
                      <td>
                        <MatchCell
                          frequencies={matchedFrequencies[license.unique_system_identifier]}
                          bands={matchedBands[license.unique_system_identifier]}
                          miles={matchedDistances[license.unique_system_identifier]}
                          sites={matchedSites[license.unique_system_identifier]}
                        />
                      </td>
                    )}
                    <td className="hidden sm:table-cell">
                      {RADIO_SERVICES[license.radio_service_code] || license.radio_service_code}
                    </td>
                    <td>
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          license.license_status === 'A'
                            ? 'bg-green-100 text-green-800'
                            : license.license_status === 'E'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {LICENSE_STATUS[license.license_status] || license.license_status}
                      </span>
                    </td>
                    <td className="hidden md:table-cell">
                      {license.grant_date
                        ? format(new Date(license.grant_date), 'MM/dd/yyyy')
                        : '—'}
                    </td>
                    <td
                      className={
                        license.expired_date &&
                        new Date(license.expired_date) < new Date()
                          ? 'text-red-600 font-medium'
                          : ''
                      }
                    >
                      {license.expired_date
                        ? format(new Date(license.expired_date), 'MM/dd/yyyy')
                        : '—'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const usi = license.unique_system_identifier
                        const status = rowActionStatus[usi]
                        const actionId = rowActionIds[usi]
                        const CheckIcon = () => (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                        )
                        return (
                          <div className="flex items-center gap-0.5">
                            <DncToggle
                              callSign={license.call_sign}
                              isDnc={dncCallSigns.has(license.call_sign)}
                              onChange={(isDnc) => applyDncChange(license.call_sign, isDnc)}
                              onMessage={(msg) => {
                                setDncToastMsg(msg)
                                setTimeout(() => setDncToastMsg(''), 3000)
                              }}
                              onError={setError}
                            />

                            {status === 'loading' || clientModalLoading ? (
                              status === 'loading' && <span className="p-2 text-gray-400 text-xs">…</span>
                            ) : status === 'error' ? (
                              <span title="Action failed" className="p-2 text-red-500 font-bold text-xs">!</span>
                            ) : (
                              <>
                                {/* Lead indicator or Add-as-Lead button */}
                                {status === 'lead' ? (
                                  actionId ? (
                                    <Link
                                      to={`/crm/leads/${actionId}`}
                                      title="Tracked as lead — click to view"
                                      className="p-2 text-green-600 hover:text-green-700 rounded hover:bg-green-50 inline-flex"
                                    >
                                      <CheckIcon />
                                    </Link>
                                  ) : (
                                    <span title="Tracked as lead" className="p-2 text-green-600 inline-flex">
                                      <CheckIcon />
                                    </span>
                                  )
                                ) : status !== 'client' ? (
                                  <button
                                    type="button"
                                    title="Add as lead"
                                    aria-label={`Add ${license.call_sign} as lead`}
                                    onClick={() => { setLeadNote(''); setLeadPriority('medium'); setLeadActionError(''); setLeadModal({ license }) }}
                                    className="p-2 text-indigo-500 hover:text-indigo-700 rounded hover:bg-indigo-50"
                                  >
                                    <UserPlusIcon />
                                  </button>
                                ) : null}

                                {/* Client indicator or Add-as-Client button */}
                                {status === 'client' ? (
                                  actionId ? (
                                    <Link
                                      to={`/crm/clients/${actionId}`}
                                      title="Linked as client — click to view"
                                      className="p-2 text-teal-600 hover:text-teal-700 rounded hover:bg-teal-50 inline-flex"
                                    >
                                      <BriefcaseIcon />
                                    </Link>
                                  ) : (
                                    <span title="Linked as client" className="p-2 text-teal-600 inline-flex">
                                      <BriefcaseIcon />
                                    </span>
                                  )
                                ) : (
                                  <button
                                    type="button"
                                    title={status === 'lead' ? 'Convert lead to client' : 'Add as client'}
                                    aria-label={`Add ${license.call_sign} as client`}
                                    onClick={() => openClientModal(license)}
                                    className="p-2 text-teal-600 hover:text-teal-800 rounded hover:bg-teal-50"
                                  >
                                    <BriefcaseIcon />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            page={searchParams.page!}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            total={totalResults}
            pageSize={searchParams.limit ?? 25}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {!loading && results.length === 0 && totalResults === 0 && (
        <div className="card text-center py-12">
          {searchTerm.trim() ? (
            <>
              <p className="text-gray-500">
                No licenses match “{searchTerm.trim()}”.
              </p>
              {/* The escape hatch has to be HERE, not only beside a result count.
                  Finding nothing is exactly when someone wants the wider search, and
                  the results card this offer used to live in does not render when
                  there are no results — so the one moment it was needed was the one
                  moment it was missing. */}
              {searchMode === 'starts' && (
                <p className="mt-1 text-sm text-gray-500">
                  That matched from the start of a word.{' '}
                  <button
                    type="button"
                    onClick={() => handleSearch()}
                    className="py-0.5 text-primary-600 hover:text-primary-800 underline"
                  >
                    Search inside words too
                  </button>
                </p>
              )}
            </>
          ) : anyFilterSet ? (
            <p className="text-gray-500">No licenses match the filters you have set.</p>
          ) : (
            <p className="text-gray-500">
              Search above, or set a filter, to find licenses
            </p>
          )}

          {/* Shown for BOTH branches above, because the date window narrows a
              text search and a filter-only search alike, and in both cases it is
              the filter nobody remembers setting — the page set it for them. */}
          {narrowingExpiry && (
            <p className="mt-2 text-sm text-gray-500">
              The expiration filter is set to{' '}
              <span className="font-medium">{narrowingExpiry.toLowerCase()}</span>.{' '}
              <button
                type="button"
                onClick={searchAllDates}
                className="py-0.5 text-primary-600 hover:text-primary-800 underline"
              >
                Search all expiration dates
              </button>
            </p>
          )}
        </div>
      )}

      {/* Add as Lead modal */}
      {leadModal && (
        <Modal
          titleId="lead-modal-title"
          title="Add as Lead"
          subtitle={`${leadModal.license.call_sign} — ${leadModal.license.entity?.entity_name || 'Unknown entity'}`}
          onClose={() => setLeadModal(null)}
          maxWidth="max-w-md"
        >
          <div className="space-y-3">
            {leadModal.license.call_sign
              && dncCallSigns.has(leadModal.license.call_sign) && (
              /* Adding a suppressed license to the working queue is not forbidden —
                 there are good reasons (a renewal they asked us to handle) — but it
                 must be a decision rather than an accident. */
              <div
                role="alert"
                className="flex gap-2 rounded border border-red-300 bg-red-50 p-2.5 text-sm text-red-800"
              >
                <span aria-hidden="true">&#9888;</span>
                <span>
                  <strong>{leadModal.license.call_sign} is on the Do Not Contact
                  list.</strong>{' '}
                  Adding it as a lead puts it in the working queue. Sends will still be
                  blocked or warned, but nobody is stopped from ringing it.
                </span>
              </div>
            )}
            <div>
              <label htmlFor="lead-priority" className="label text-sm">Priority</label>
              <select
                id="lead-priority"
                className="input"
                value={leadPriority}
                onChange={(e) => setLeadPriority(e.target.value as typeof leadPriority)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label htmlFor="lead-note" className="label text-sm">Note (optional)</label>
              <textarea
                id="lead-note"
                className="input"
                rows={3}
                placeholder="Why are you tracking this lead? Any relevant context…"
                maxLength={5000}
                value={leadNote}
                onChange={(e) => setLeadNote(e.target.value)}
              />
            </div>

            {leadActionError && (
              <p className="text-sm text-red-600" role="alert">{leadActionError}</p>
            )}
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={() => setLeadModal(null)}
              className="btn btn-secondary"
              disabled={leadActionLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddAsLead}
              className="btn btn-primary"
              disabled={leadActionLoading}
            >
              {leadActionLoading ? 'Adding…' : 'Add as Lead'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* Add as Client modal — link to existing client or create new */}
      {clientModal && (
        <Modal
          titleId="client-modal-title"
          title="Add as Client"
          subtitle={`${clientModal.license.call_sign} — ${clientModal.license.entity?.entity_name || 'Unknown entity'}`}
          onClose={() => setClientModal(null)}
          maxWidth="max-w-lg"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Similar clients found. Link this license to an existing client, or create a new one.
            </p>
            <div className="space-y-2">
              {clientModal.matches.map((m) => (
                <label
                  key={m.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    clientModalSelectedId === m.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="client-select"
                    value={m.id}
                    checked={clientModalSelectedId === m.id}
                    onChange={() => setClientModalSelectedId(m.id)}
                    className="mt-0.5 h-4 w-4 text-primary-600"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900">{m.entity_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {m.account_status} · {m.license_count} license{m.license_count !== 1 ? 's' : ''}
                      {m.contact_name ? ` · ${m.contact_name}` : ''}
                    </p>
                  </div>
                </label>
              ))}
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  clientModalSelectedId === 'new'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="client-select"
                  value="new"
                  checked={clientModalSelectedId === 'new'}
                  onChange={() => setClientModalSelectedId('new')}
                  className="mt-0.5 h-4 w-4 text-primary-600"
                />
                <div>
                  <p className="font-medium text-sm text-gray-900">Create new client</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    New account for {clientModal.license.entity?.entity_name || clientModal.license.call_sign}
                  </p>
                </div>
              </label>
            </div>
            {clientModalError && (
              <p className="text-sm text-red-600" role="alert">{clientModalError}</p>
            )}
          </div>
          <ModalFooter>
            <button type="button" onClick={() => setClientModal(null)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => doAddAsClient(clientModal.license, clientModalSelectedId)}
              className="btn btn-primary"
            >
              {clientModalSelectedId === 'new' ? 'Create Client' : 'Link to Client'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* Bulk add as leads modal */}
      {bulkLeadModal && (
        <Modal
          titleId="bulk-lead-modal-title"
          title={
            bulkSkipSuppressed && suppressedAddableUsis.length > 0
              ? `Add ${addableUsis.length - suppressedAddableUsis.length} Results as Leads`
              : `Add ${addableUsis.length} Results as Leads`
          }
          subtitle="Already-tracked licenses will be skipped automatically."
          onClose={() => { setBulkLeadModal(false); setBulkResult(null) }}
          maxWidth="max-w-lg"
        >
          <div className="space-y-3">
            {suppressedAddableUsis.length > 0 && (
              /* Bulk add never consulted the Do Not Contact list, so a whole page of
                 results could pull suppressed licenses into the queue silently. The
                 count is stated rather than just filtered, because "we quietly did
                 something different from what the button said" is its own problem. */
              <div
                role="alert"
                className="rounded border border-red-300 bg-red-50 p-2.5 text-sm text-red-800"
              >
                <div className="flex gap-2">
                  <span aria-hidden="true">&#9888;</span>
                  <span>
                    <strong>
                      {suppressedAddableUsis.length} of {addableUsis.length}
                      {' '}are on the Do Not Contact list.
                    </strong>
                  </span>
                </div>
                <label className="mt-2 flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={bulkSkipSuppressed}
                    onChange={(e) => setBulkSkipSuppressed(e.target.checked)}
                  />
                  <span>
                    Skip them — add{' '}
                    {addableUsis.length - suppressedAddableUsis.length} instead
                  </span>
                </label>
              </div>
            )}
            <div>
              <label htmlFor="bulk-priority" className="label text-sm">Priority</label>
              <select
                id="bulk-priority"
                className="input"
                value={bulkPriority}
                onChange={(e) => setBulkPriority(e.target.value as typeof bulkPriority)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label htmlFor="bulk-note" className="label text-sm">Note (optional)</label>
              <textarea
                id="bulk-note"
                className="input"
                rows={3}
                maxLength={5000}
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
              />
              <p className="text-xs text-gray-400 mt-1">
                Pre-filled with search description. Edit or clear as needed.
              </p>
            </div>

            {bulkResult && (
              <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800" role="status">
                Done: {bulkResult.added} added, {bulkResult.skipped} already tracked.
              </div>
            )}
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={() => { setBulkLeadModal(false); setBulkResult(null) }}
              className="btn btn-secondary"
              disabled={bulkLoading}
            >
              {bulkResult ? 'Close' : 'Cancel'}
            </button>
            {!bulkResult && (
              <button
                type="button"
                onClick={handleBulkAddLeads}
                className="btn btn-primary"
                disabled={bulkLoading}
              >
                {bulkLoading ? 'Adding…' : `Add ${results.length} as Leads`}
              </button>
            )}
          </ModalFooter>
        </Modal>
      )}

      {/* Save search modal */}
      {saveModalOpen && (
        <Modal
          titleId="save-search-modal-title"
          title="Save This Search"
          onClose={() => { setSaveModalOpen(false); setSaveName(''); setSaveError('') }}
          maxWidth="max-w-sm"
        >
          <div>
            <label htmlFor="save-search-name" className="label text-sm">Search name</label>
            <input
              id="save-search-name"
              type="text"
              className="input"
              placeholder="e.g. Active CO licenses expiring 2026"
              maxLength={100}
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveSearch()}
              autoFocus
            />
            {saveError && <p className="text-sm text-red-600 mt-1" role="alert">{saveError}</p>}
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={() => { setSaveModalOpen(false); setSaveName(''); setSaveError('') }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="button" onClick={handleSaveSearch} className="btn btn-primary">
              Save
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
